import React, { useCallback, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import {
  PrecisionManufacturingRounded,
  HubRounded,
  CheckCircleRounded,
} from "@mui/icons-material";
import { dt } from "../../themes/redesignTokens";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { setMachineContext } from "../chatBotSlice";
import type { ChatActionRequest } from "../models/chatBotModels";
import useTranslation from "../../hooks/useTranslation";

// ============================================================
// ChatContextSwitchButtons — renders compact pill buttons for
// nav.switch_machine and nav.switch_platform action requests
// (tier-0). Clicking a button switches the MIDAS chat context.
// No confirmation dialog needed (approval_tier: 0).
// ============================================================

const midas = {
  gold: "#D4A017",
  goldSoft: "rgba(212, 160, 23, 0.08)",
  goldBorder: "rgba(212, 160, 23, 0.25)",
  successGreen: "#27AE60",
};

interface ChatContextSwitchButtonsProps {
  uiActions: ChatActionRequest[];
}

const ChatContextSwitchButtons: React.FC<ChatContextSwitchButtonsProps> = ({
  uiActions,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const machineContextLoading = useAppSelector(
    (s) => s.chatBot.machineContextLoading
  );
  const isPhone = useMediaQuery("(max-width:600px)");

  // Track which button was clicked (by action_id)
  const [clickedId, setClickedId] = useState<string | null>(null);

  const handleSwitchMachine = useCallback(
    (action: ChatActionRequest) => {
      if (clickedId) return; // Already clicked one
      const machineId = action.values?.machine_id;
      const machineName = action.values?.machine_name || "";
      if (!machineId) return;

      setClickedId(action.action_id);
      dispatch(
        setMachineContext({
          machineId,
          displayData: {
            model: machineName,
            serialNumber: "",
          },
        })
      );
    },
    [dispatch, clickedId]
  );

  const handleSwitchPlatform = useCallback(
    (action: ChatActionRequest) => {
      if (clickedId) return;
      setClickedId(action.action_id);
      dispatch(setMachineContext(null));
    },
    [dispatch, clickedId]
  );

  const handleClick = useCallback(
    (action: ChatActionRequest) => {
      if (action.action_type === "nav.switch_machine") {
        handleSwitchMachine(action);
      } else if (action.action_type === "nav.switch_platform") {
        handleSwitchPlatform(action);
      }
    },
    [handleSwitchMachine, handleSwitchPlatform]
  );

  if (!uiActions || uiActions.length === 0) return null;

  return (
    <Box
      sx={{
        ml: "36px",
        mt: "8px",
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        maxWidth: isPhone ? "90%" : "85%",
      }}
    >
      {uiActions.map((action) => {
        const isClicked = clickedId === action.action_id;
        const isDisabled = clickedId !== null && !isClicked;
        const isMachine = action.action_type === "nav.switch_machine";
        const isPlatform = action.action_type === "nav.switch_platform";

        // Use bot-provided label as primary text, fallback to values or translation
        const label = action.label
          || (isMachine ? (action.values?.machine_name || translate("midas.contextSwitch.machine")) : "")
          || (isPlatform ? translate("midas.contextSwitch.switchToPlatform") : "")
          || action.display?.title
          || translate("midas.contextSwitch.machine");

        const Icon = isClicked
          ? CheckCircleRounded
          : isMachine
          ? PrecisionManufacturingRounded
          : HubRounded;

        return (
          <Box
            key={action.action_id}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-label={label}
            aria-disabled={isDisabled || machineContextLoading}
            onClick={() => {
              if (!isDisabled && !machineContextLoading) {
                handleClick(action);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!isDisabled && !machineContextLoading) {
                  handleClick(action);
                }
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              py: "6px",
              borderRadius: dt.radiusPill,
              border: `1px solid ${isClicked ? midas.successGreen : midas.goldBorder}`,
              backgroundColor: isClicked
                ? "rgba(39, 174, 96, 0.06)"
                : midas.goldSoft,
              cursor: isDisabled || machineContextLoading ? "default" : "pointer",
              opacity: isDisabled ? 0.45 : 1,
              transition: "all 0.2s ease",
              userSelect: "none",
              ...(!isDisabled &&
                !isClicked &&
                !machineContextLoading && {
                  "&:hover": {
                    backgroundColor: "rgba(212, 160, 23, 0.14)",
                    borderColor: midas.gold,
                    boxShadow: "0 1px 6px rgba(212, 160, 23, 0.15)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }),
            }}
          >
            <Icon
              sx={{
                fontSize: "16px",
                color: isClicked ? midas.successGreen : midas.gold,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 600,
                color: isClicked ? midas.successGreen : dt.textPrimary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: isPhone ? "180px" : "220px",
                lineHeight: 1.3,
              }}
            >
              {label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ChatContextSwitchButtons;
